import { getDefaultAvatar } from "@repo/fika-design-system/avatar"
import { createMemo, For, Show } from "solid-js"
import { Info } from "@/designSystem/patterns/info"
import { createFragment, type FragmentOf, graphql } from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import { useRelativeTime } from "@/lib/use-relative-time"
import { DashboardSection } from "./_dashboard-section"
import {
  reactionAvatarClass,
  reactionAvatarImageClass,
  reactionContentClass,
  reactionEmojiClass,
  reactionEmojisClass,
  reactionHeaderClass,
  reactionItemClass,
  reactionMetaClass,
  reactionPostTitleClass,
  reactionsListClass,
  reactionTimeClass,
  reactionUserNameClass,
} from "./_recent-reactions.css"

export const RecentReactionsFragment = graphql(`
  fragment RecentReactionsFragment on Query {
    recentReactions(limit: 5) {
      postId
      postTitle
      totalCount
      userNames
      otherCount
      emojis
      latestReactionAt
      latestReactorProfileImage
    }
  }
`)

export function RecentReactions(props: {
  data: FragmentOf<typeof RecentReactionsFragment>
  isPending: boolean
}) {
  const { LL } = useI18nContext()
  const fragmentData = createFragment(RecentReactionsFragment, () => props.data)
  const reactions = createMemo(() => fragmentData().recentReactions ?? [])

  return (
    <DashboardSection
      title={LL().dashboard.recentReactions()}
      iconName="heart"
      hue="rose"
    >
      <Show
        when={!props.isPending}
        fallback={<Info>{LL().common.loading()}</Info>}
      >
        <Show
          when={reactions().length > 0}
          fallback={<Info>{LL().dashboard.noReactionsYet()}</Info>}
        >
          <div class={reactionsListClass}>
            <For each={reactions()}>
              {reaction => <ReactionItem reaction={reaction} />}
            </For>
          </div>
        </Show>
      </Show>
    </DashboardSection>
  )
}

function ReactionItem(props: {
  reaction: {
    postId: string
    postTitle: string
    totalCount: number
    userNames: string[]
    otherCount: number
    emojis: string[]
    latestReactionAt: Date
    latestReactorProfileImage: string | null
  }
}) {
  const { LL, locale } = useI18nContext()
  const relativeTime = useRelativeTime(() => props.reaction.latestReactionAt)
  const topEmojis = createMemo(() => props.reaction.emojis.slice(0, 3))

  const avatarUrl = createMemo(
    () =>
      props.reaction.latestReactorProfileImage ??
      getDefaultAvatar(props.reaction.postId),
  )

  const namesWithOthers = createMemo(() => {
    const names = props.reaction.userNames
    const others = props.reaction.otherCount
    if (others > 0) {
      return [...names, LL().dashboard.othersCount(others)]
    }
    return names
  })

  const formattedParts = createMemo(() => {
    const list = namesWithOthers()
    if (list.length === 0) return []

    const formatter = new Intl.ListFormat(locale(), {
      style: "long",
      type: "conjunction",
    })
    return formatter.formatToParts(list)
  })

  return (
    <div class={reactionItemClass}>
      <div class={reactionAvatarClass}>
        <img src={avatarUrl()} alt="" class={reactionAvatarImageClass} />
        <span class={reactionEmojisClass}>
          <For each={topEmojis()}>
            {emoji => <span class={reactionEmojiClass}>{emoji}</span>}
          </For>
        </span>
      </div>
      <div class={reactionContentClass}>
        <div class={reactionHeaderClass}>
          <span class={reactionPostTitleClass}>
            <For each={formattedParts()}>
              {part => (
                <Show when={part.type === "element"} fallback={part.value}>
                  <strong class={reactionUserNameClass}>{part.value}</strong>
                </Show>
              )}
            </For>{" "}
            {LL().dashboard.reactedToYourPost()}
          </span>
          <span class={reactionTimeClass}>{relativeTime()}</span>
        </div>
        <span class={reactionMetaClass}>{props.reaction.postTitle}</span>
      </div>
    </div>
  )
}
